/* GETTING A PICTURE READY TO GO UP TO HER CRM.
 *
 * Vercel refuses any request over 4.5 MB, and a picture travels as base64,
 * which adds a third. So 3 MB is the ceiling for what leaves the browser, and a
 * phone photo (4–8 MB is normal) is shrunk here first — longest side 2400px,
 * which is more than a slide or a post will ever show.
 *
 * Shrinking keeps the format where it can: a PNG logo stays a PNG so its
 * transparent background survives. GIF and SVG are never redrawn — a canvas
 * would flatten the animation and rasterise the vector — so those go up as
 * they are, or not at all if they are too big.
 */
export const UPLOAD_MAX = 3 * 1024 * 1024;
export const LONGEST_SIDE = 2400;

const PICTURE = /^image\/(png|jpe?g|gif|webp|svg\+xml)$/i;
const REDRAWABLE = /^image\/(png|jpe?g|webp)$/i;

/** What will happen to this file, decided before any work is done. */
export function planUpload(file) {
  const type = String(file?.type || "");
  const size = Number(file?.size || 0);
  if (!PICTURE.test(type)) return { ok: false, error: "That isn't a picture Brill can send — use a PNG, JPG, GIF, WebP or SVG." };
  if (!size) return { ok: false, error: "That file is empty." };
  if (size <= UPLOAD_MAX) return { ok: true, shrink: false };
  if (!REDRAWABLE.test(type)) return { ok: false, error: "That picture is over 3 MB, and a GIF or SVG can't be shrunk without spoiling it. Try a smaller one." };
  return { ok: true, shrink: true };
}

/** The size to redraw at: longest side capped, proportions kept, never enlarged. */
export function shrinkSize(width, height, longest = LONGEST_SIDE) {
  const big = Math.max(width, height);
  if (!big || big <= longest) return { width, height };
  const k = longest / big;
  return { width: Math.round(width * k), height: Math.round(height * k) };
}

const readBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = () => reject(new Error("Couldn't read that picture."));
  r.readAsDataURL(blob);
});

async function shrink(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("Couldn't open that picture to shrink it."));
      i.src = url;
    });
    const { width, height } = shrinkSize(img.naturalWidth, img.naturalHeight);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d").drawImage(img, 0, 0, width, height);
    const type = /png/i.test(file.type) ? "image/png" : /webp/i.test(file.type) ? "image/webp" : "image/jpeg";
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, type, 0.86));
    if (!blob) throw new Error("Couldn't shrink that picture.");
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * The picture, ready for /api/ghl's media upload action.
 * @returns {{ ok: true, name, contentType, data } | { ok: false, error }}
 */
export async function prepareImage(file) {
  const ready = await prepareBlob(file);
  if (!ready.ok) return ready;
  try {
    return { ok: true, name: ready.name, contentType: ready.contentType, data: await readBase64(ready.blob) };
  } catch (err) {
    return { ok: false, error: err.message };
  }
}

/**
 * The same shrink, handed back as the picture itself — for Brill's own picture
 * storage, which the browser writes straight into (no base64, no /api hop).
 * The 3 MB ceiling stays: it is what keeps a phone photo light on her page.
 * @returns {{ ok: true, blob, name, contentType } | { ok: false, error }}
 */
export async function prepareBlob(file) {
  const plan = planUpload(file);
  if (!plan.ok) return plan;
  try {
    const blob = plan.shrink ? await shrink(file) : file;
    if (blob.size > UPLOAD_MAX) {
      return { ok: false, error: "That picture is still over 3 MB after shrinking. Try a smaller one." };
    }
    return { ok: true, blob, name: file.name || "picture", contentType: blob.type || file.type };
  } catch (err) {
    return { ok: false, error: err.message };
  }
}
